import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import {
  EXPENSE_PAYMENT_METHODS,
  ExpenseStatus,
  PERMISSIONS,
  type ExpensePaymentMethod,
  type ExpenseSummary,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useExpenseCategoriesQuery,
  useExpenseSuppliersQuery,
  useExpensesQuery,
} from '../api/expenses.api';
import { ExpenseStatusBadge } from '../components/ExpenseBadges';

const PAGE_SIZE = 20;

/**
 * What the business has spent running itself.
 *
 * Every expense here is already paid, which is why no column on this page mentions a balance, a due
 * date or a settlement state — there is nothing outstanding to show. A voided expense keeps its
 * amount and says plainly that it no longer counts.
 *
 * Search reaches the EXP reference and the supplier name the document kept. It does not reach the
 * payee, the notes or the provider's reference: a search term travels in the address bar, and those
 * three are the fields nobody expects to find there.
 */
export function ExpensesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches } = useBranch();
  const { format } = useCurrency();
  const { can } = usePermissions();

  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [status, setStatus] = useState<ExpenseStatus | ''>('');
  const [method, setMethod] = useState<ExpensePaymentMethod | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useExpensesQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    branchId,
    categoryId,
    supplierId,
    status,
    method,
    dateFrom,
    dateTo,
  });

  // Every category, including the retired ones: a filter has to reach historical expenses.
  const categories = useExpenseCategoriesQuery({ pageSize: 100 });
  const suppliers = useExpenseSuppliersQuery({ pageSize: 100 });

  const canManage = can(PERMISSIONS.EXPENSES_MANAGE);
  const canManageCategories = can(PERMISSIONS.EXPENSES_CATEGORIES_MANAGE);

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const columns: Column<ExpenseSummary>[] = [
    {
      key: 'reference',
      header: t('expenses.fields.reference'),
      primary: true,
      render: (expense) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {expense.reference}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {expense.categoryName}
          </span>
        </span>
      ),
    },
    {
      key: 'expenseDate',
      header: t('expenses.fields.expenseDate'),
      render: (expense) => (
        <span className="text-ink text-sm" dir="ltr">
          {expense.expenseDate}
        </span>
      ),
    },
    {
      key: 'branch',
      header: t('expenses.fields.branch'),
      render: (expense) => (
        <span className="text-muted text-sm" dir="auto">
          {expense.branchName}
        </span>
      ),
    },
    {
      key: 'paidTo',
      header: t('expenses.fields.paidTo'),
      render: (expense) => (
        <span className="text-muted text-sm" dir="auto">
          {expense.supplierName ?? expense.payee ?? '—'}
        </span>
      ),
    },
    {
      key: 'method',
      header: t('expenses.fields.method'),
      render: (expense) => (
        <span className="text-muted text-sm">{t(`expenses.methods.${expense.method}`)}</span>
      ),
    },
    {
      key: 'amount',
      header: t('expenses.fields.amount'),
      align: 'end',
      render: (expense) => (
        <span className="flex flex-col items-end">
          <span
            className={
              expense.status === ExpenseStatus.VOIDED
                ? 'text-muted line-through'
                : 'text-ink font-medium'
            }
            dir="ltr"
          >
            {format(expense.amountMinor)}
          </span>
          {expense.status === ExpenseStatus.VOIDED ? (
            <span className="text-muted text-xs">{t('expenses.noLongerCounts')}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('expenses.fields.status'),
      render: (expense) => <ExpenseStatusBadge status={expense.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (expense) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('expenses.openNamed', { reference: expense.reference })}
          onClick={() => void navigate(`/finance/expenses/${expense.id}`)}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('expenses.title')}
        subtitle={t('expenses.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canManageCategories ? (
              <Button
                variant="secondary"
                onClick={() => void navigate('/finance/expenses/categories')}
              >
                {t('expenses.categories.title')}
              </Button>
            ) : null}
            {canManage ? (
              <Button onClick={() => void navigate('/finance/expenses/new')}>
                {t('expenses.record')}
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <CardHeader
          title={t('expenses.title')}
          subtitle={data ? t('expenses.count', { count: data.pagination.totalItems }) : undefined}
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => onFilter(setSearch)(event.target.value)}
            placeholder={t('expenses.searchPlaceholder')}
            aria-label={t('expenses.searchLabel')}
          />
          <Select
            options={[
              { value: '', label: t('expenses.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('expenses.fields.branch')}
            onChange={(event) => onFilter(setBranchId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('expenses.filters.allCategories') },
              ...(categories.data?.items ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
            value={categoryId}
            aria-label={t('expenses.fields.category')}
            onChange={(event) => onFilter(setCategoryId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('expenses.filters.allSuppliers') },
              ...(suppliers.data?.items ?? []).map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
              })),
            ]}
            value={supplierId}
            aria-label={t('expenses.fields.supplier')}
            onChange={(event) => onFilter(setSupplierId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('expenses.filters.allStatuses') },
              ...Object.values(ExpenseStatus).map((value) => ({
                value,
                label: t(`expenses.status.${value}`),
              })),
            ]}
            value={status}
            aria-label={t('expenses.fields.status')}
            onChange={(event) => onFilter(setStatus)(event.target.value as ExpenseStatus | '')}
          />
          <Select
            options={[
              { value: '', label: t('expenses.filters.allMethods') },
              ...EXPENSE_PAYMENT_METHODS.map((value) => ({
                value,
                label: t(`expenses.methods.${value}`),
              })),
            ]}
            value={method}
            aria-label={t('expenses.fields.method')}
            onChange={(event) =>
              onFilter(setMethod)(event.target.value as ExpensePaymentMethod | '')
            }
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => onFilter(setDateFrom)(event.target.value)}
            aria-label={t('expenses.filters.dateFrom')}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => onFilter(setDateTo)(event.target.value)}
            aria-label={t('expenses.filters.dateTo')}
          />
        </div>

        <DataTable
          caption={t('expenses.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(expense) => expense.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('expenses.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Receipt}
          emptyTitle={t('expenses.empty')}
          emptyDescription={t('expenses.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
